import React, { useEffect } from "react";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbJsonLd } from "../components/Seo";
import { PAGE_SEO, WA_LINKS } from "../data/siteData";
import { ArrowUpRight } from "lucide-react";

const APPROACH = [
  "Consultant and advisor first",
  "Clear commercial scope",
  "Customer-action thinking",
  "Strategic visual communication",
  "AI-supported analysis and production",
  "Human review and judgment",
  "Practical handoff",
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className="bg-white">
      <Seo
        {...PAGE_SEO.about}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ])}
      />
      <Navbar />

      <section className="relative bg-[#0A192F] text-white pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              About AXIS
            </span>
          </div>
          <h1
            data-testid="about-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.0] max-w-3xl"
          >
            Strategic thinking <span className="text-[#D4AF37]">made practical.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
            AXIS Business Solutions helps growing organizations clarify what they are trying to
            communicate, improve how customers experience the business, and turn
            recommendations into focused, usable assets.
          </p>
        </div>
      </section>

      <section data-testid="about-founder" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <img
              src="/assets/davin-campbell.jpg"
              alt="Davin Campbell, founder of AXIS Business Solutions"
              className="w-full max-w-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.3)] ring-1 ring-[#D4AF37]/20"
            />
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#0A192F]">
              Davin Campbell
            </h2>
            <p className="mt-6 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl">
              Davin brings more than 20 years of experience across marketing leadership, visual
              communications management, multi-location brand rollouts, graphic design, sign
              production, brand development, customer experience, and business operations.
            </p>
            <p className="mt-4 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl">
              That combination shapes the AXIS approach: diagnose the real problem, define the
              customer action, establish a responsible scope, and create work that can function
              in the real business—not only look good in a presentation.
            </p>

            <h3 className="mt-10 font-display text-xl md:text-2xl font-semibold text-[#0A192F]">
              AXIS approach
            </h3>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {APPROACH.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-[#0A192F]">
                  <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-10 font-display text-lg md:text-xl text-[#1E3A8A] font-medium">
              Technology provides insight. Experience provides judgment. AXIS combines both.
            </p>
          </div>
        </div>
      </section>

      <section data-testid="about-cta" className="bg-[#0A192F] text-white py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">
            Not sure where to begin?
          </h2>
          <div className="mt-8">
            <a
              href={WA_LINKS.recommend}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
            >
              Get My AXIS Recommendation <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
